import { z } from 'zod';

import {
  TJsonValidationParams,
  TTimestampValidationParams,
  TValidationParams,
} from '@/shared/types/validation.types';

export const DEFAULT_ERROR_KEY = 'default';
export const MAX_LENGTH_ERROR_KEY = 'max_length_error';
export const INVALID_JSON_ERROR_KEY = 'invalid_json_error';
export const INVALID_JSON_VALUES_ERROR_KEY = 'invalid_json_values_error';
export const INVALID_URL_ERROR_KEY = 'invalid_url_error';
export const INVALID_EMAIL_ERROR_KEY = 'invalid_email_error';
export const REQUIRED_ARRAY_ERROR_KEY = 'required_array_error';
export const RESET_TIMESTAMP_VALUE = '0001-01-01T00:00:00';
export const END_BEFORE_START_ERROR_KEY = 'end_before_start_period';
export const EXPIRATION_OR_DURATION_ERROR_KEY = 'expiration_or_duration';
export const DURATION_INCOMPLETE_ERROR_KEY = 'duration_incomplete';
export const QUANTITY_NEEDS_PERIOD_ERROR_KEY = 'quantity_needs_period';
export const INVALID_FORMAT_ERROR_KEY = 'invalid_format';

const applyValidationParamsToSchema = (
  schema: z.ZodString,
  params?: TValidationParams,
): z.ZodString => {
  if (params?.regex) {
    // nosemprep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
    const regexp = new RegExp(params.regex);
    schema = schema.regex(regexp, { message: INVALID_FORMAT_ERROR_KEY });
  }

  if (params?.maxLength) {
    schema = schema.max(params.maxLength, {
      message: MAX_LENGTH_ERROR_KEY,
    });
  }

  if (params?.url) {
    schema = schema.url({ message: INVALID_URL_ERROR_KEY });
  }

  return schema;
};

export const optionalString = (params?: TValidationParams) => {
  const schema = applyValidationParamsToSchema(z.string().trim(), params);

  return schema
    .nullable()
    .optional()
    .transform((val) => (val === null ? '' : val));
};

export const optionalNumber = z
  .union([
    z.string().transform((value) => (value === '' ? undefined : Number(value))),
    z.number(),
  ])
  .optional();

// Timestamp cannot be empty due to backend limitation so we use a string schema that allows null or undefined
export const optionalTimestamp = (params?: TTimestampValidationParams) => {
  const { resettable, ...restParams } = params || {};
  const schema = applyValidationParamsToSchema(z.string().trim(), restParams)
    .nullable()
    .optional();

  // if DB already has a timestamp, we cannot set it to null, so we use a sentinel value that the backend understands as resetting the timestamp
  if (resettable) {
    return schema.transform((val) =>
      val === undefined || val === '' || val === null
        ? RESET_TIMESTAMP_VALUE
        : val,
    );
  }

  return schema.transform((val) =>
    val === undefined || val === '' ? null : val,
  );
};

export const requiredString = (params?: TValidationParams) => {
  return applyValidationParamsToSchema(
    z
      .string({ message: DEFAULT_ERROR_KEY })
      .trim()
      .min(1, { message: DEFAULT_ERROR_KEY }),
    params,
  );
};

// The message is an `error` namespace key, like the other validation messages.
export const emailString = (params?: TValidationParams) =>
  requiredString(params).email(INVALID_EMAIL_ERROR_KEY);

export const requiredArray = <T extends z.ZodTypeAny>(itemSchema: T) => {
  return z.array(itemSchema).min(1, {
    message: REQUIRED_ARRAY_ERROR_KEY,
  });
};

const validateJsonValues = (obj: Object, allowedValues: string[]): boolean => {
  const allowedSet = new Set(allowedValues);

  const checkValue = (value: unknown): boolean => {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.values(value).every((val) => checkValue(val));
    }

    if (Array.isArray(value)) {
      return value.every((val) => checkValue(val));
    }

    return allowedSet.has(String(value));
  };

  return Object.values(obj).every((value) => checkValue(value));
};

export const requiredJsonString = (params?: TJsonValidationParams) => {
  const { allowedValues, ...requiredStringParams } = params || {};

  return requiredString(requiredStringParams).refine(
    (value) => {
      let parsedValue;
      try {
        parsedValue = JSON.parse(value);
      } catch {
        return false;
      }

      return !(
        allowedValues && !validateJsonValues(parsedValue, allowedValues)
      );
    },
    (value) => {
      try {
        JSON.parse(value);
        return { message: INVALID_JSON_VALUES_ERROR_KEY };
      } catch {
        return { message: INVALID_JSON_ERROR_KEY };
      }
    },
  );
};

export const codeInputSchema = (
  codeLength: number = 6,
  i18nErrorKey: string = DEFAULT_ERROR_KEY,
) =>
  z
    .string()
    .trim()
    // nosemprep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
    .regex(new RegExp(`^[0-9]{${codeLength}}$`), { message: i18nErrorKey });

export const passwordInputSchema = (minLength: number = 10) =>
  z
    .string()
    .min(minLength, { message: 'no_message' })
    .regex(/[A-Z]/, { message: 'no_message' })
    .regex(/[a-z]/, { message: 'no_message' })
    .regex(/[0-9]/, { message: 'no_message' })
    .regex(/[!@#$%^&*(),.?":{}|<>]/, { message: 'no_message' });

export const validateUnique = <T>(
  propertyName: keyof T,
  errorMessage: string = `${String(propertyName)} must be unique`,
) => {
  return (items: T[], ctx: z.RefinementCtx) => {
    const valueMap = new Map();

    items.forEach((item, index) => {
      const value = item[propertyName];

      if (valueMap.has(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: errorMessage,
          path: [index, propertyName as string],
        });
      } else {
        valueMap.set(value, index);
      }
    });
  };
};
